import React, { useEffect, useState } from 'react';
import styled from '@emotion/native';
import { View } from 'react-native';
import { useApolloClient, useQuery } from '@apollo/client';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ScrollableView from '@/shared/components/ScrollableView';
import { H3 } from '@/shared/components/StyledText';
import { sand, white } from '@/shared/styles/colors';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { GET_ADMIN_OVERVIEW } from '@/shared/queries/admin';
import { QueryResult } from '@/shared/types/generated';
import OverviewPanel from './OverviewPanel';
import StylistsPanel from './StylistsPanel';
import UsersPanel from './UsersPanel';
import MessagesPanel from './MessagesPanel';
import BookingsPanel from './BookingsPanel';
import PromoCodesPanel from './PromoCodesPanel';
import { ActionLink, SegmentedTabs, Small } from './AdminUI';
import { ago } from './format';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'stylists', label: 'Stylists' },
  { value: 'messages', label: 'Messages' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'users', label: 'Users' },
  { value: 'promos', label: 'Promo codes' },
] as const;

export type AdminTab = (typeof TABS)[number]['value'];
export type OpenTab = (tab: AdminTab, filter?: string) => void;

const isAdminTab = (value: unknown): value is AdminTab =>
  TABS.some((tab) => tab.value === value);

const AdminDashboard = () => {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const client = useApolloClient();
  const params = useLocalSearchParams<{ tab?: string; filter?: string }>();
  const tab: AdminTab = isAdminTab(params.tab) ? params.tab : 'overview';
  const filter = params.filter || undefined;

  // Counts for the tab badges; shared with the overview's own query
  const { data: overviewData } = useQuery<QueryResult<'adminOverview'>>(
    GET_ADMIN_OVERVIEW,
    { fetchPolicy: 'cache-and-network' },
  );
  const overview = overviewData?.adminOverview;
  // Things waiting on someone, per tab
  const badges: Partial<Record<AdminTab, number | null | undefined>> = {
    stylists: overview
      ? overview.listedStylists - overview.bookableStylists
      : null,
    messages: overview?.unopenedConversations,
    bookings: overview?.pendingRequests,
  };

  const [updatedAt, setUpdatedAt] = useState(() => Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [, setTick] = useState(0);
  // Keep "Updated x ago" honest while the page sits open
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);
  // Each tab loads fresh when opened
  useEffect(() => setUpdatedAt(Date.now()), [tab, filter]);

  // Keep the tab in the URL so a refresh or shared link opens the same view
  const openTab: OpenTab = (next, nextFilter) =>
    router.setParams({ tab: next, filter: nextFilter ?? '' });

  const refresh = async () => {
    setRefreshing(true);
    try {
      await client.refetchQueries({ include: 'active' });
      setUpdatedAt(Date.now());
    } finally {
      setRefreshing(false);
    }
  };

  const updatedLabel =
    Date.now() - updatedAt < 60_000 ? 'just now' : ago(updatedAt);

  return (
    <Container isDesktop={isDesktop}>
      {!isDesktop && <MobileTitle>Dashboard</MobileTitle>}
      <Header isDesktop={isDesktop}>
        <View style={{ flexShrink: 1 }}>
          <SegmentedTabs
            selected={tab}
            onSelect={(next) => openTab(next)}
            tabs={TABS.map((item) => ({
              ...item,
              badge: badges[item.value] ?? null,
            }))}
          />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Small>Updated {updatedLabel}</Small>
          <ActionLink
            text={refreshing ? 'Refreshing…' : 'Refresh'}
            onPress={refresh}
          />
        </View>
      </Header>

      {tab === 'overview' && (
        <OverviewPanel isDesktop={isDesktop} onOpenTab={openTab} />
      )}
      {tab === 'stylists' && (
        <StylistsPanel isDesktop={isDesktop} initialFilter={filter} />
      )}
      {tab === 'messages' && (
        <MessagesPanel isDesktop={isDesktop} initialFilter={filter} />
      )}
      {tab === 'bookings' && (
        <BookingsPanel isDesktop={isDesktop} initialFilter={filter} />
      )}
      {tab === 'users' && <UsersPanel isDesktop={isDesktop} />}
      {tab === 'promos' && <PromoCodesPanel isDesktop={isDesktop} />}
    </Container>
  );
};

const Container = styled(ScrollableView)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    paddingTop: 28,
    paddingBottom: 60,
    paddingHorizontal: isDesktop ? 50 : 16,
    backgroundColor: isDesktop ? white : sand,
  }),
);

const MobileTitle = styled(H3)({
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: 20,
});

const Header = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  justifyContent: 'space-between',
  alignItems: isDesktop ? 'center' : 'flex-start',
  gap: 12,
  marginBottom: 28,
}));

export default AdminDashboard;
